import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { analytics } from '../utils/analytics';
import { API_CONFIG } from '../config/api';

interface OrderItem {
  name: string;
  quantity: number;
  price?: number;
}

interface Order {
  id: number;
  shop_name: string;
  items: OrderItem[];
  total_price?: number;
  status: string;
  created_at: string;
}

export const Dashboard: React.FC = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [userId, setUserId] = useState<number | null>(null);

  useEffect(() => {
    // Redirect to home if not authenticated
    if (!isAuthenticated) {
      navigate('/');
      return;
    }

    // Track dashboard view
    analytics.pageView('/dashboard');

    // Extract user ID from email hash (temp solution)
    if (user?.email) {
      // Simple hash for demo - in production, this would come from JWT
      const hash = user.email.split('').reduce((acc: number, char: string) => {
        return ((acc << 5) - acc) + char.charCodeAt(0);
      }, 0);
      setUserId(Math.abs(hash) % 1000 + 1);
    }
  }, [isAuthenticated, navigate, user]);

  useEffect(() => {
    if (userId) {
      fetchOrders();
    }
  }, [userId]);

  const fetchOrders = async () => {
    if (!userId) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_CONFIG.API_URL}/api/orders?userId=${userId}`);
      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
      }
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncFromGmail = async () => {
    if (!userId) return;

    setSyncing(true);
    try {
      const response = await fetch(`${API_CONFIG.API_URL}/api/orders/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Synced ${data.newOrders} new orders!`);
        await fetchOrders();
      } else {
        alert('Failed to sync orders. Make sure you have order emails in Gmail.');
      }
    } catch (error) {
      console.error('Failed to sync from Gmail:', error);
      alert('Failed to sync from Gmail');
    } finally {
      setSyncing(false);
    }
  };

  const handleCreateTestOrder = async () => {
    if (!userId) return;

    try {
      const response = await fetch(`${API_CONFIG.API_URL}/api/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          shopName: 'Blue Bottle Coffee',
          items: [
            { name: 'Espresso', quantity: 2, price: 4.00 },
            { name: 'Croissant', quantity: 1, price: 3.50 },
          ],
          totalPrice: 11.50,
        }),
      });

      if (response.ok) {
        alert('Test order created!');
        await fetchOrders();
      }
    } catch (error) {
      console.error('Failed to create test order:', error);
      alert('Failed to create test order');
    }
  };

  const handleLogout = () => {
    logout();
    analytics.track('button_click', { button: 'logout' });
    navigate('/');
  };

  if (!isAuthenticated || !user) {
    return null; // Will redirect in useEffect
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-amber-900">
            ☕ taptap.coffee
          </h1>
          <button
            onClick={handleLogout}
            className="text-gray-600 hover:text-gray-900 font-medium transition"
          >
            Logout
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Welcome Card */}
        <div className="bg-white rounded-3xl shadow-xl p-8 mb-8">
          <div className="flex items-center gap-4 mb-6">
            {user.picture && (
              <img
                src={user.picture}
                alt={user.name}
                className="w-16 h-16 rounded-full"
              />
            )}
            <div>
              <h2 className="text-3xl font-bold text-gray-900">
                Welcome back, {user.name || 'Coffee Lover'}!
              </h2>
              <p className="text-gray-600">{user.email}</p>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
            <h3 className="font-semibold text-amber-900 mb-2">
              🎉 Authentication Successful!
            </h3>
            <p className="text-amber-800 text-sm">
              Your Gmail account is now connected. We're working on email-based ordering.
              Soon you'll be able to order coffee by sending an email!
            </p>
          </div>
        </div>

        {/* Orders Section */}
        <div className="bg-white rounded-3xl shadow-xl p-8">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-bold text-gray-900">Your Orders</h3>
            <div className="flex gap-3">
              <button
                onClick={handleSyncFromGmail}
                disabled={syncing}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition disabled:opacity-50"
              >
                {syncing ? '⏳ Syncing...' : '📧 Sync from Gmail'}
              </button>
              <button
                onClick={handleCreateTestOrder}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition"
              >
                ➕ Create Test Order
              </button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="text-4xl mb-4">⏳</div>
              <p className="text-gray-600">Loading orders...</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📬</div>
              <p className="text-gray-600 mb-2">No orders yet</p>
              <p className="text-sm text-gray-500 mb-4">
                Try creating a test order or syncing from Gmail!
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((order) => (
                <div
                  key={order.id}
                  className="border border-gray-200 rounded-xl p-6 hover:border-amber-300 transition"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h4 className="font-bold text-lg text-gray-900">
                        ☕ {order.shop_name}
                      </h4>
                      <p className="text-sm text-gray-500">
                        {new Date(order.created_at).toLocaleString()}
                      </p>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-medium ${
                        order.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : order.status === 'confirmed'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {order.status}
                    </span>
                  </div>

                  <div className="space-y-2 mb-4">
                    {order.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-gray-700">
                          {item.quantity}x {item.name}
                        </span>
                        {item.price && (
                          <span className="text-gray-600">
                            ${(item.quantity * item.price).toFixed(2)}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>

                  {order.total_price && (
                    <div className="border-t pt-3 flex justify-between font-bold">
                      <span>Total</span>
                      <span>${order.total_price.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Debug Info (Development Only) */}
        {import.meta.env.DEV && (
          <div className="mt-8 bg-gray-100 rounded-xl p-6">
            <h4 className="font-bold text-gray-700 mb-2">Debug Info</h4>
            <pre className="text-xs text-gray-600 overflow-auto">
              {JSON.stringify(user, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};
